import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import accepts from 'attr-accept';
import Button from '@saucelabs/sl-component-buttons';
import { Loader } from '@saucelabs/sl-component-loader';
import { actions as notifications } from '@saucelabs/sl-container-notifications';
import { Tooltip } from '@saucelabs/ds-tooltip';

import Styles from './ImageInjectionPopover.styles';
import operations from '../operations';
import selectors from '../selectors';
import { IMAGE_INJECTION_MAX_FILE_SIZE } from '../constants';
import { MANUAL_TEST_EVENTS, trackEvent } from './../../../tracking';

const { imageInjectionPopoverSelector } = selectors;

const context = classNames.bind(Styles);

const ImageInjectionPopover = ({
  fileName,
  isLoading,
  notifyDanger,
  onSelectFile,
  previewImageURL,
}) => {
  const inputFile = useRef(null);

  useEffect(() => {
    trackEvent(MANUAL_TEST_EVENTS.SHOW_CAMERA_INJECTION_POPOVER);
  }, []);

  const handleFileUpload = (event) => {
    onSelectFile(event.target.files[0]);

    inputFile.current.value = '';
  };

  const handleFileValidation = (event) => {
    const { target: { files, accept } } = event;
    const fileSize = files[0].size;

    // IMAGE_INJECTION_MAX_FILE_SIZE = 5242880 (5MB)
    if (fileSize > IMAGE_INJECTION_MAX_FILE_SIZE || !(accepts(files[0], accept))) {
      const errorMessage = 'Image could not be uploaded. The file size should be 5 MB or less and the supported formats are JPEG and PNG.';
      notifyDanger(errorMessage);
    } else {
      handleFileUpload(event);
    }
  };

  const isPreviewImageAvailable = !!previewImageURL;

  return (
    <div
      className={context('default', { 'preview-image': isPreviewImageAvailable })}
      data-qa="image-injection-popover"
    >
      <div className={Styles.headline}>Camera Upload</div>
      { !isPreviewImageAvailable && (
        <div className={Styles.info}>
          Choose an image to upload (png or jpg, maximum file size 5MB).
        </div>
      )}
      { isPreviewImageAvailable && (
        <figure
          className={Styles['thumbnail-wrapper']}
          data-qa="image-injection-thumbnail"
        >
          <img
            alt="uploaded file"
            className={Styles.thumbnail}
            data-ot-ignore
            src={previewImageURL}
          />
          <Tooltip
            delay={300}
            placement="top"
            title={fileName}
          >
            <figcaption className={Styles['file-name']}>{fileName}</figcaption>
          </Tooltip>
        </figure>
      )}
      <div className={context('upload-wrapper', {
        loading: isLoading,
      })}
      >
        <input
          ref={inputFile}
          accept="image/jpeg,image/png"
          className={Styles['input-file']}
          data-qa="image-injection-popover-input"
          onChange={handleFileValidation}
          type="file"
        />
        <Loader
          data-qa="image-injection-popover-loader"
          fontSize="1.75em"
          show={isLoading}
        >
          <Button
            category={isPreviewImageAvailable ? 'link' : 'info'}
            className={context({
              foo: isPreviewImageAvailable,
            })}
            data-qa="image-injection-popover-upload-button"
            onClick={() => inputFile.current.click()}
            size="sm"
          >
            {isPreviewImageAvailable ? 'Choose Another' : 'Choose Image'}
          </Button>
        </Loader>
      </div>
    </div>
  );
};

ImageInjectionPopover.propTypes = {
  fileName: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  notifyDanger: PropTypes.func.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  previewImageURL: PropTypes.string.isRequired,
};

const mapDispatchToProps = {
  onSelectFile: operations.injectImage,
  notifyDanger: notifications.notifyDanger,
};

export default connect(imageInjectionPopoverSelector, mapDispatchToProps)(ImageInjectionPopover);

export {
  ImageInjectionPopover,
};
